import { Tab } from '@headlessui/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/shadcn/ui/select';
import { Fragment, ReactNode, useEffect, useState } from 'react';
import { cn } from '../../utils/shadcn';

export interface TabGroup {
  tab: ReactNode;
  panel: ReactNode;
  disabledMessage?: ReactNode;
}
interface TabsProps {
  tabs: TabGroup[];
  defaultIndex?: number;
  selectedIndex?: number;
  onTabSelected?: (tabGroup: TabGroup, index: number) => void;
  overflow?: boolean;
  tabGroupClassName?: string;
  tabListClassName?: string;
  showSelect?: boolean;
}
export function Tabs({
  tabs,
  defaultIndex,
  selectedIndex,
  onTabSelected,
  overflow = true,
  tabListClassName,
  tabGroupClassName,
  showSelect = true,
}: TabsProps) {
  const [internalSelectedIndex, setInternalSelectedIndex] = useState(defaultIndex ?? selectedIndex ?? 0);

  useEffect(() => {
    if (typeof selectedIndex === 'number' && selectedIndex >= 0) {
      setInternalSelectedIndex(() => selectedIndex);
    }
  }, [selectedIndex]);

  useEffect(() => {
    if (onTabSelected) {
      onTabSelected(tabs[internalSelectedIndex], internalSelectedIndex);
    }
  }, [internalSelectedIndex]);

  const firstEnabledIndex = tabs.findIndex((tab) => !tab.disabledMessage);

  return (
    <Tab.Group
      defaultIndex={defaultIndex ?? firstEnabledIndex}
      selectedIndex={internalSelectedIndex}
      onChange={setInternalSelectedIndex}
    >
      {showSelect && (
        <div className={cn('sm:hidden', tabGroupClassName)}>
          <Select
            value={String(internalSelectedIndex)}
            onValueChange={(value) => setInternalSelectedIndex(Number(value))}
          >
            <SelectTrigger>
              <SelectValue>{tabs[internalSelectedIndex].tab}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {tabs.map(({ tab }, i) => (
                <SelectItem key={i} value={String(i)}>
                  {tab}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {tabs.length > 1 && (
        <Tab.List
          className={cn(
            'border border-border border-b flex items-center gap-4 rounded-xl mb-4 p-2 px-4',
            tabListClassName,
            showSelect ? 'hidden sm:flex' : '',
          )}
        >
          {tabs.map(({ tab, disabledMessage }, i) => (
            <Tab key={i} as={Fragment}>
              {({ selected }) => (
                <button
                  data-tab={`tab-${i}`}
                  className={cn(
                    selected ? 'text-muted-foreground font-bold' : 'text-muted hover:text-foreground',
                    'px-0 text-sm',
                  )}
                >
                  {tab}
                </button>
              )}
            </Tab>
          ))}
        </Tab.List>
      )}
      <Tab.Panels>
        {tabs.map(({ panel, disabledMessage }, i) => (
          <Tab.Panel key={i} className={overflow ? 'overflow-hidden' : ''}>
            {disabledMessage ?? panel}
          </Tab.Panel>
        ))}
      </Tab.Panels>
    </Tab.Group>
  );
}
